import type { ComboBoxInputItemType } from "@ui-kit/form/ComboBox/ComboBox.props";
import type { SelectInputItemType } from "@ui-kit/form/Select/Select.props";
import type { PhoneCountryType } from "@shared/lib/libphonenumber/api";
import { ResponsiveValue } from "@shared/theme/types";
import { TextProps } from "@shared/components/ui-kit/typography/Text/Text.web";
import React from "react";

export const FORM_FIELD_TYPES = {
  TEXT: "text",
  EMAIL: "email",
  PASSWORD: "password",
  NUMBER: "number",
  PHONE: "phone",
  TEXTAREA: "textarea",
  RICHTEXT: "richtext",
  FONT_FAMILY: "font_family",
  SELECT: "select",
  MULTISELECT: "multiselect",
  DATE: "date",
  DATERANGE: "daterange",
  DATETIME: "datetime",
  CHECKBOX: "checkbox",
  RADIO: "radio",
  TOGGLE: "toggle",
  SEARCH: "search",
  SEARCH_ADDRESS: "search_address",
  SEARCH_EVENT: "search_event",
  SEARCH_PRODUCT: "search_product",
  SEARCH_MEMBERSHIP: "search_membership",
  FILE: "file",
  IMAGE: "image",
  COLOR: "color",
  REPEATER: "repeater",
  GROUP: "group",
  SECTION: "section",
  CUSTOM: "custom",
  FIELD_ARRAY: "field_array",
} as const;

export type FormFieldType =
  (typeof FORM_FIELD_TYPES)[keyof typeof FORM_FIELD_TYPES];

export interface ValidationRule {
  type:
    | "required"
    | "min"
    | "max"
    | "matches"
    | "email"
    | "oneOf"
    | "test"
    | "phone";
  value?: any; // eslint-disable-line @typescript-eslint/no-explicit-any
  message: string;
  ref?: string;
}

interface BaseFormFieldConfig {
  name: string;
  label?: string;
  placeholder?: string;
  helpText?: string;
  defaultValue?: unknown;
  hidden?: boolean;
  hideLabel?: boolean;
  /** Controls whether the optional indicator is shown for non-required fields */
  showOptionalTag?: boolean;
  width?: string;
  pt?: string;
  testId?: string;
  required?: boolean | string;
  validationRules?: ValidationRule[];
  /** Disable the field */
  disabled?: boolean;
  /** Tooltip text shown on hover (useful for explaining why field is disabled) */
  tooltip?: string;
  /** Align items within the field container (e.g., "center" for avatar images) */
  alignItems?: "flex-start" | "center" | "flex-end" | "stretch";
}

/**
 * Text input field
 */
export interface TextFormFieldConfig extends BaseFormFieldConfig {
  type: typeof FORM_FIELD_TYPES.TEXT;
  inputType?: "text" | "url";
  autoComplete?: string;
  maxLength?: number;
  minLength?: number;
}

/**
 * Email input field
 */
export interface EmailFormFieldConfig extends BaseFormFieldConfig {
  type: typeof FORM_FIELD_TYPES.EMAIL;
  autoComplete?: string;
  maxLength?: number;
}

/**
 * Password input field
 */
export interface PasswordFormFieldConfig extends BaseFormFieldConfig {
  type: typeof FORM_FIELD_TYPES.PASSWORD;
  autoComplete?: string;
  maxLength?: number;
  minLength?: number;
}

/**
 * Number input field
 */
/**
 * Input addon configuration for number fields
 */
export interface NumberInputAddon {
  /** Type of addon */
  type: "text" | "icon";
  /** Text content (for type: "text", e.g., "$", "%") */
  content?: string;
  /** Icon name from Icon component (for type: "icon") */
  iconName?: string;
}

/**
 * Dynamic number validation - references another field's value
 * @example
 * // Price must be >= value field
 * min: { field: "value", message: "Price must be at least the gift card value" }
 */
export interface DynamicNumberValidation {
  /** Field name to reference (can be relative like "value" for sibling in repeater) */
  field: string;
  /** Custom error message */
  message?: string;
  /** Optional offset to add/subtract from referenced value */
  offset?: number;
}

/**
 * Number validation can be:
 * - A static number
 * - A static number with custom message
 * - A dynamic reference to another field
 */
export type NumberValidation =
  | number
  | { value: number; message: string }
  | DynamicNumberValidation;

/**
 * Number field - formatted number input with optional addons
 * Uses NumericFormat for proper number formatting
 */
export interface NumberFormFieldConfig extends BaseFormFieldConfig {
  type: typeof FORM_FIELD_TYPES.NUMBER;
  /** Minimum value - can be static or reference another field */
  min?: NumberValidation;
  /** Maximum value - can be static or reference another field */
  max?: NumberValidation;
  /** Step value for native number inputs (not used with NumericFormat) */
  step?: number;
  /** Decimal places (default: 2) */
  decimalScale?: number;
  /** Allow negative values (default: false) */
  allowNegative?: boolean;
  /** Left addon (icon or text, e.g., "$") */
  leftAddon?: NumberInputAddon;
  /** Right addon (icon or text, e.g., "%") */
  rightAddon?: NumberInputAddon;
}

/**
 * Textarea field
 */
export interface TextareaFormFieldConfig extends BaseFormFieldConfig {
  type: typeof FORM_FIELD_TYPES.TEXTAREA;
  rows?: number;
  maxLength?: number;
  minLength?: number;
  resize?: "none" | "both" | "horizontal" | "vertical";
}

/**
 * Rich text editor field (Quill-based)
 */
export interface RichtextFormFieldConfig extends BaseFormFieldConfig {
  type: typeof FORM_FIELD_TYPES.RICHTEXT;
  /** Custom toolbar options for Quill editor */
  toolbarOptions?: Array<string | Array<string | object>>;
  /** Enable custom image handler with modal */
  enableCustomImageHandler?: boolean;
}

/**
 * * Font family selector field
 */
export interface FontFamilyFormFieldConfig extends BaseFormFieldConfig {
  type: typeof FORM_FIELD_TYPES.FONT_FAMILY;
}
/**
 * Phone number input field
 */
export interface PhoneFormFieldConfig extends BaseFormFieldConfig {
  type: typeof FORM_FIELD_TYPES.PHONE;
  defaultCountry?: string;
  onCountryChange?: (country: PhoneCountryType) => void;
}

/**
 * Select dropdown (single select)
 */
export interface SelectFormFieldConfig extends BaseFormFieldConfig {
  type: typeof FORM_FIELD_TYPES.SELECT;
  options: SelectInputItemType[];
  valueKey?: string; // Extract specific property (e.g., "id")
  match?: (item: SelectInputItemType) => JSX.Element;
  optionsMatch?: (item: SelectInputItemType) => JSX.Element;
  isLoading?: boolean;
  hasMore?: boolean;
  handleLoadMore?: () => void;
}

/**
 * Multi-select dropdown
 */
export interface MultiSelectFormFieldConfig extends BaseFormFieldConfig {
  type: typeof FORM_FIELD_TYPES.MULTISELECT;
  options: ComboBoxInputItemType[];
  valueKey?: string; // Extract specific property (e.g., "id")
  match?: (item: ComboBoxInputItemType) => JSX.Element;
  optionsMatch?: (item: ComboBoxInputItemType) => JSX.Element;
  isLoading?: boolean;
  hasMore?: boolean;
  handleLoadMore?: () => void;
}

/**
 * Date picker (single date)
 */
export interface DateFormFieldConfig extends BaseFormFieldConfig {
  type: typeof FORM_FIELD_TYPES.DATE;
  minDate?: Date;
  maxDate?: Date;
  dateFormat?: string;
  timezone?: string;
  showQuickMenu?: boolean;
  inputWidth?: string;
}

/**
 * Date range picker
 */
export interface DateRangeFormFieldConfig extends BaseFormFieldConfig {
  type: typeof FORM_FIELD_TYPES.DATERANGE;
  minDate?: Date;
  maxDate?: Date;
  dateFormat?: string;
  timezone?: string;
  showQuickMenu?: boolean;
  inputWidth?: string;
}

/**
 * Datetime picker (native datetime-local input)
 *
 * Uses the native HTML5 datetime-local input for combined date and time selection.
 * Returns value in ISO format suitable for API submissions.
 *
 * @example
 * {
 *   type: FORM_FIELD_TYPES.DATETIME,
 *   name: "settlement_date",
 *   label: "Settlement Date",
 *   required: true,
 * }
 */
export interface DatetimeFormFieldConfig extends BaseFormFieldConfig {
  type: typeof FORM_FIELD_TYPES.DATETIME;
  /** Minimum datetime value (ISO string or datetime-local format) */
  min?: string;
  /** Maximum datetime value (ISO string or datetime-local format) */
  max?: string;
}

/**
 * Checkbox field
 */
export interface CheckboxFormFieldConfig extends BaseFormFieldConfig {
  type: typeof FORM_FIELD_TYPES.CHECKBOX;
  checkboxLabel?: string; // Label for the checkbox itself
  checkboxLabelProps?: TextProps;
}

/**
 * Radio group field
 */
export interface RadioFormFieldConfig extends BaseFormFieldConfig {
  type: typeof FORM_FIELD_TYPES.RADIO;
  options: Array<{ id: string | number; label: string; value: unknown }>;
  direction?: "row" | "column";
}

/**
 * Toggle/Switch field
 */
export interface ToggleFormFieldConfig extends BaseFormFieldConfig {
  type: typeof FORM_FIELD_TYPES.TOGGLE;
  toggleLabel?: string;
}

export interface FileFormFieldConfig extends BaseFormFieldConfig {
  type: typeof FORM_FIELD_TYPES.FILE;
  accept?: string;
  multiple?: boolean;
  maxSizeBytes?: number;
  maxSizeErrorMessage?: string;
  onFileSelect?: (
    files: File | File[] | null,
    fileList?: FileList | null
  ) => void;
  /** Image-specific: minimum width in pixels (required for image uploads) */
  minWidth?: number;
  /** Image-specific: minimum height in pixels (required for image uploads) */
  minHeight?: number;
  /** Image-specific: maximum width in pixels (required for image uploads) */
  maxWidth?: number;
  /** Image-specific: maximum height in pixels (required for image uploads) */
  maxHeight?: number;
}

/**
 * Counter configuration for displaying counts below search
 */
export interface SearchFieldCounterConfig {
  /** Label for the counter (e.g., "Events", "Ticket types") - can be string or function for pluralization */
  label: string | ((count: number) => string);
  /** Function to calculate count from current values and optional sub-item selections */
  getValue: (
    items: unknown[],
    subItemSelections?: Map<number, number[]>
  ) => number;
}

/**
 * Sub-item type for nested selections (ticket types, product attributes, membership levels)
 */
export interface SubItemType {
  id: number;
  name: string;
}

// ============================================================================
// SERVICE-BASED PAGINATION
// ============================================================================

/**
 * Service with usePaginated hook for search fields.
 *
 * Any service that returns from useModelService can be used here, including:
 * - useVenueEventsService
 * - useVenueProductsService
 * - useVenueBasedMembershipGroupService
 *
 * The type is intentionally flexible to accept any service structure.
 * Item type matching happens at runtime in the field components.
 *
 * @example
 * // In parent component:
 * const eventService = useVenueEventsService(venue?.id);
 *
 * // In config:
 * {
 *   type: FORM_FIELD_TYPES.SEARCH_EVENT,
 *   name: "events",
 *   service: eventService,
 *   searchParams: { starts_on__gte: today, only_parents: true },
 * }
 */
export interface SearchServiceConfig {
  /**
   * The usePaginated method from a service (e.g., from useModelService).
   * Accepts loose typing to support various service implementations.
   */
  usePaginated: (config: {
    params?: Record<string, unknown>;
    options?: { enabled?: boolean };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  }) => SearchServiceResult<any>;
  /** Allow additional methods from the service object */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

/**
 * Result type from a service's usePaginated hook.
 * Compatible with React Query's useInfiniteQuery return type.
 * Uses loose typing to accept various paginated response structures.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export interface SearchServiceResult<T = any> {
  /** Paginated data from React Query */
  data?: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    pages: Array<{ results: T[] } & Record<string, any>>;
    pageParams?: unknown[];
  };
  /** Whether a fetch is in progress */
  isFetching: boolean;
  /** Whether there are more pages to fetch */
  hasNextPage?: boolean;
  /** Function to fetch the next page */
  fetchNextPage: () => void;
  /** Whether next page fetch is in progress */
  isFetchingNextPage: boolean;
  /** Allow additional properties from the hook result */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

/**
 * Props passed to renderSelectedItem function
 */
export interface SearchFieldSelectedItemProps<T> {
  /** The original item from search results */
  item: T;
  /** The stored form value for this item */
  value: unknown;
  /** Index in the selected items array */
  index: number;
  /** Callback to remove this item */
  onRemove: () => void;
}

/**
 * Generic async search field with customizable data fetching
 * Supports both single-select and multi-select modes via the `single` prop
 *
 * @example
 * // Single select (default)
 * {
 *   type: FORM_FIELD_TYPES.SEARCH,
 *   name: "venue_id",
 *   label: "Venue",
 *   fetchOptions: async (query) => {
 *     const response = await venueRepository.list({ params: { q: query } });
 *     return response;
 *   },
 *   getOptionLabel: (item) => item.name,
 *   getOptionValue: (item) => item.id,
 * }
 *
 * @example
 * // Multi-select with selected items list
 * {
 *   type: FORM_FIELD_TYPES.SEARCH,
 *   name: "events",
 *   single: false,
 *   fetchOptions: async (query) => eventRepository.search(query),
 *   getOptionLabel: (item) => item.name,
 *   renderSelectedItem: ({ item, onRemove }) => (
 *     <Box p="md" bg="bg.muted" borderRadius="md">
 *       <Text fontWeight="bold">{item.name}</Text>
 *       <IconButton aria-label="Remove" onClick={onRemove} />
 *     </Box>
 *   ),
 *   counters: [
 *     { label: "Events", getValue: (values) => values.length }
 *   ],
 * }
 */
export interface SearchFormFieldConfig<T = Record<string, unknown>>
  extends BaseFormFieldConfig {
  type: typeof FORM_FIELD_TYPES.SEARCH;
  /** Single select (true, default) vs multi-select (false) */
  single?: boolean;
  /** Async function to fetch options based on search query */
  fetchOptions: (query: string) => Promise<T[]>;
  /** Extract display label from option */
  getOptionLabel: (item: T) => string;
  /** Extract value to store in form (e.g., ID). If not provided, stores the full object */
  getOptionValue?: (item: T) => unknown;
  /** Custom template for rendering options in dropdown */
  listItemTemplate?: (item: T) => JSX.Element;
  /** Minimum characters before search triggers (default: 2) */
  minSearchLength?: number;
  /** Debounce delay in milliseconds (default: 300) */
  debounceMs?: number;
  /** Placeholder text for search input */
  searchPlaceholder?: string;
  /** Text shown when no results found */
  noResultsText?: string;
  /** Text shown when minimum characters not met */
  minCharsText?: string;

  // ===== NEW: Selected items list features =====

  /** Render template for each selected item below the search (multi-select only) */
  renderSelectedItem?: (
    props: SearchFieldSelectedItemProps<T>
  ) => React.ReactNode;
  /** Counter configuration to display counts (e.g., "5 events, 12 ticket types") */
  counters?: SearchFieldCounterConfig[];
  /** Hide the default ComboBox selected tags when using renderSelectedItem */
  hideSelectedTags?: boolean;
  /** Reset input after selecting an item (useful for "add to list" patterns) */
  resetOnSelect?: boolean;
}

/**
 * Event search item type with domain-specific fields
 */
export interface EventSearchItemType extends ComboBoxInputItemType {
  id: number;
  name: string;
  starts_on?: string;
  ends_on?: string;
  timezone?: string;
  status?: string;
  is_recurring_parent?: boolean;
  tickettype_set?: SubItemType[];
}

/**
 * Product search item type with domain-specific fields
 */
export interface ProductSearchItemType extends ComboBoxInputItemType {
  id: number;
  name: string;
  product_attributes?: SubItemType[];
}

/**
 * Membership search item type with domain-specific fields
 */
export interface MembershipSearchItemType extends ComboBoxInputItemType {
  id: number;
  name: string;
  membership_levels?: SubItemType[];
}

/**
 * Props passed to renderSelectedItem for domain search fields
 */
export interface SearchSelectedItemProps<T> {
  /** The selected item */
  item: T;
  /** Callback to remove this item */
  onRemove: () => void;
  /** Sub-items for this item (e.g., ticket types) */
  subItems?: SubItemType[];
  /** Currently selected sub-item IDs */
  selectedSubItemIds?: number[];
  /** Callback to toggle a sub-item */
  onToggleSubItem?: (subItemId: number, isSelected: boolean) => void;
  /** Whether the item is still loading */
  isLoading?: boolean;
}

/**
 * Search Event field - domain-specific search for events with built-in dropdown templates
 *
 * ## 3-Layer Architecture
 *
 * **Layer 1 (Base):** At its simplest, just fetches and selects events. Outputs full objects by default.
 * ```typescript
 * { type: SEARCH_EVENT, name: "events", fetchOptions: searchEvents }
 * // Output: [{ id: 1, name: "...", starts_on: "...", tickettype_set: [...] }]
 * ```
 *
 * **Layer 2 (Domain):** Built-in dropdown template shows event dates, status badges automatically.
 *
 * **Layer 3 (Enhanced):** Opt-in features via props:
 * - `showSelectedBelow`: Display selected items below the search
 * - `withSubItems`: Enable ticket type toggles
 * - `getFormValue`: Transform output to custom structure
 * - `counters`: Show selection counts
 *
 * @example
 * // Simple - just fetch and select (outputs full objects)
 * {
 *   type: FORM_FIELD_TYPES.SEARCH_EVENT,
 *   name: "events",
 *   fetchOptions: searchEvents,
 * }
 *
 * @example
 * // With selected items display
 * {
 *   type: FORM_FIELD_TYPES.SEARCH_EVENT,
 *   name: "events",
 *   fetchOptions: searchEvents,
 *   showSelectedBelow: true,
 *   selectedItemsDisplay: "cards",
 * }
 *
 * @example
 * // Full permissions config with sub-item toggles
 * {
 *   type: FORM_FIELD_TYPES.SEARCH_EVENT,
 *   name: "permissions",
 *   fetchOptions: searchEvents,
 *   showSelectedBelow: true,
 *   withSubItems: true,
 *   getFormValue: (items, subs) => transformToPermissions(items, subs),
 * }
 */
export interface SearchEventFormFieldConfig extends BaseFormFieldConfig {
  type: typeof FORM_FIELD_TYPES.SEARCH_EVENT;

  // === CORE DATA FETCHING (choose one approach) ===

  /**
   * Service instance with usePaginated hook (PREFERRED).
   * Pass any service that extends useModelService (e.g., useVenueEventsService).
   *
   * @example
   * const eventService = useVenueEventsService(venue?.id);
   * { service: eventService, searchParams: { starts_on__gte: today } }
   */
  service?: SearchServiceConfig;

  /**
   * Additional params to pass to the service's usePaginated call.
   * Only used when `service` is provided.
   *
   * @example
   * searchParams: { starts_on__gte: today, only_parents: true }
   */
  searchParams?: Record<string, unknown>;

  /** Simple async function to fetch all events based on search query (non-paginated) */
  fetchOptions?: (query: string) => Promise<EventSearchItemType[]>;

  // === SELECTION MODE ===
  /** Multi-select (default: true) or single-select */
  multiple?: boolean;

  // === OUTPUT CONFIGURATION ===
  /**
   * Transform selected items to form value.
   * - Not provided: outputs full objects (default)
   * - Function: custom transform (items, subSelections?) => yourFormat
   */
  getFormValue?: (
    items: EventSearchItemType[],
    subItemSelections?: Map<number, number[]>
  ) => unknown;

  // === DISPLAY SELECTED ITEMS (Layer 3) ===
  /** Show selected items below the search field (default: false) */
  showSelectedBelow?: boolean;
  /** How to display selected items: "tags" | "cards" | "list" (default: "cards" when showSelectedBelow=true) */
  selectedItemsDisplay?: "tags" | "cards" | "list";
  /** Custom render for each selected item */
  renderSelectedItem?: (
    props: SearchSelectedItemProps<EventSearchItemType>
  ) => React.ReactNode;

  // === SUB-ITEMS (Layer 3, opt-in) ===
  /** Enable sub-item toggles (ticket types for events) */
  withSubItems?: boolean;
  /** Override sub-items key (default: "tickettype_set" for events) */
  subItemsKey?: string;

  // === COUNTERS (Layer 3, opt-in) ===
  /** Counter configs to display selection counts */
  counters?: SearchFieldCounterConfig[];

  // === CALLBACKS ===
  /** Called when selection changes - can update other form fields */
  onSelectionChange?: (
    items: EventSearchItemType[],
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    formContext: any
  ) => void;

  // === FILTERING ===
  /** Filter items from dropdown (e.g., exclude already selected elsewhere) */
  filterOptions?: (item: EventSearchItemType) => boolean;

  // === EXISTING PROPS ===
  /** Custom template for dropdown items (overrides built-in domain template) */
  listItemTemplate?: (item: EventSearchItemType) => React.ReactNode;
  /** Filter events by type */
  eventTypes?: ("single" | "parent" | "template")[];
}

/**
 * Search Product field - domain-specific search for products with built-in dropdown templates
 *
 * ## 3-Layer Architecture
 *
 * **Layer 1 (Base):** At its simplest, just fetches and selects products. Outputs full objects by default.
 * ```typescript
 * { type: SEARCH_PRODUCT, name: "products", fetchOptions: searchProducts }
 * // Output: [{ id: 1, name: "...", product_attributes: [...] }]
 * ```
 *
 * **Layer 2 (Domain):** Built-in dropdown template shows product name.
 *
 * **Layer 3 (Enhanced):** Opt-in features via props.
 *
 * @example
 * // Simple - just fetch and select
 * {
 *   type: FORM_FIELD_TYPES.SEARCH_PRODUCT,
 *   name: "products",
 *   fetchOptions: searchProducts,
 * }
 *
 * @example
 * // With product attribute toggles
 * {
 *   type: FORM_FIELD_TYPES.SEARCH_PRODUCT,
 *   name: "product_permissions",
 *   fetchOptions: searchProducts,
 *   showSelectedBelow: true,
 *   withSubItems: true,
 * }
 */
export interface SearchProductFormFieldConfig extends BaseFormFieldConfig {
  type: typeof FORM_FIELD_TYPES.SEARCH_PRODUCT;

  // === CORE DATA FETCHING (choose one approach) ===

  /**
   * Service instance with usePaginated hook (PREFERRED).
   * Pass any service that extends useModelService (e.g., useVenueProductsService).
   *
   * @example
   * const productService = useVenueProductsService();
   * { service: productService, searchParams: { is_active: true } }
   */
  service?: SearchServiceConfig;

  /**
   * Additional params to pass to the service's usePaginated call.
   * Only used when `service` is provided.
   */
  searchParams?: Record<string, unknown>;

  /** Simple async function to fetch all products based on search query (non-paginated) */
  fetchOptions?: (query: string) => Promise<ProductSearchItemType[]>;

  // === SELECTION MODE ===
  /** Multi-select (default: true) or single-select */
  multiple?: boolean;

  // === OUTPUT CONFIGURATION ===
  /**
   * Transform selected items to form value.
   * - Not provided: outputs full objects (default)
   * - Function: custom transform (items, subSelections?) => yourFormat
   */
  getFormValue?: (
    items: ProductSearchItemType[],
    subItemSelections?: Map<number, number[]>
  ) => unknown;

  // === DISPLAY SELECTED ITEMS (Layer 3) ===
  /** Show selected items below the search field (default: false) */
  showSelectedBelow?: boolean;
  /** How to display selected items: "tags" | "cards" | "list" */
  selectedItemsDisplay?: "tags" | "cards" | "list";
  /** Custom render for each selected item */
  renderSelectedItem?: (
    props: SearchSelectedItemProps<ProductSearchItemType>
  ) => React.ReactNode;

  // === SUB-ITEMS (Layer 3, opt-in) ===
  /** Enable sub-item toggles (product attributes) */
  withSubItems?: boolean;
  /** Override sub-items key (default: "product_attributes" for products) */
  subItemsKey?: string;

  // === COUNTERS (Layer 3, opt-in) ===
  /** Counter configs to display selection counts */
  counters?: SearchFieldCounterConfig[];

  // === CALLBACKS ===
  /** Called when selection changes */
  onSelectionChange?: (
    items: ProductSearchItemType[],
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    formContext: any
  ) => void;

  // === FILTERING ===
  /** Filter items from dropdown */
  filterOptions?: (item: ProductSearchItemType) => boolean;

  // === EXISTING PROPS ===
  /** Custom template for dropdown items */
  listItemTemplate?: (item: ProductSearchItemType) => React.ReactNode;
}

/**
 * Search Membership field - domain-specific search for membership groups with built-in dropdown templates
 *
 * ## 3-Layer Architecture
 *
 * **Layer 1 (Base):** At its simplest, just fetches and selects memberships. Outputs full objects by default.
 * ```typescript
 * { type: SEARCH_MEMBERSHIP, name: "memberships", fetchOptions: searchMemberships }
 * // Output: [{ id: 1, name: "...", membership_levels: [...] }]
 * ```
 *
 * **Layer 2 (Domain):** Built-in dropdown template shows membership name.
 *
 * **Layer 3 (Enhanced):** Opt-in features via props.
 *
 * @example
 * // Simple - just fetch and select
 * {
 *   type: FORM_FIELD_TYPES.SEARCH_MEMBERSHIP,
 *   name: "memberships",
 *   fetchOptions: searchMemberships,
 * }
 *
 * @example
 * // With membership level toggles
 * {
 *   type: FORM_FIELD_TYPES.SEARCH_MEMBERSHIP,
 *   name: "membership_permissions",
 *   fetchOptions: searchMemberships,
 *   showSelectedBelow: true,
 *   withSubItems: true,
 * }
 */
export interface SearchMembershipFormFieldConfig extends BaseFormFieldConfig {
  type: typeof FORM_FIELD_TYPES.SEARCH_MEMBERSHIP;

  // === CORE DATA FETCHING (choose one approach) ===

  /**
   * Service instance with usePaginated hook (PREFERRED).
   * Pass any service that extends useModelService (e.g., useVenueBasedMembershipGroupService).
   *
   * @example
   * const membershipService = useVenueBasedMembershipGroupService();
   * { service: membershipService, searchParams: { is_active: true } }
   */
  service?: SearchServiceConfig;

  /**
   * Additional params to pass to the service's usePaginated call.
   * Only used when `service` is provided.
   */
  searchParams?: Record<string, unknown>;

  /** Simple async function to fetch all memberships based on search query (non-paginated) */
  fetchOptions?: (query: string) => Promise<MembershipSearchItemType[]>;

  // === SELECTION MODE ===
  /** Multi-select (default: true) or single-select */
  multiple?: boolean;

  // === OUTPUT CONFIGURATION ===
  /**
   * Transform selected items to form value.
   * - Not provided: outputs full objects (default)
   * - Function: custom transform (items, subSelections?) => yourFormat
   */
  getFormValue?: (
    items: MembershipSearchItemType[],
    subItemSelections?: Map<number, number[]>
  ) => unknown;

  // === DISPLAY SELECTED ITEMS (Layer 3) ===
  /** Show selected items below the search field (default: false) */
  showSelectedBelow?: boolean;
  /** How to display selected items: "tags" | "cards" | "list" */
  selectedItemsDisplay?: "tags" | "cards" | "list";
  /** Custom render for each selected item */
  renderSelectedItem?: (
    props: SearchSelectedItemProps<MembershipSearchItemType>
  ) => React.ReactNode;

  // === SUB-ITEMS (Layer 3, opt-in) ===
  /** Enable sub-item toggles (membership levels) */
  withSubItems?: boolean;
  /** Override sub-items key (default: "membership_levels" for memberships) */
  subItemsKey?: string;

  // === COUNTERS (Layer 3, opt-in) ===
  /** Counter configs to display selection counts */
  counters?: SearchFieldCounterConfig[];

  // === CALLBACKS ===
  /** Called when selection changes */
  onSelectionChange?: (
    items: MembershipSearchItemType[],
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    formContext: any
  ) => void;

  // === FILTERING ===
  /** Filter items from dropdown */
  filterOptions?: (item: MembershipSearchItemType) => boolean;

  // === EXISTING PROPS ===
  /** Custom template for dropdown items */
  listItemTemplate?: (item: MembershipSearchItemType) => React.ReactNode;
}

/**
 * Search Address field
 */
export interface SearchAddressFormFieldConfig extends BaseFormFieldConfig {
  type: typeof FORM_FIELD_TYPES.SEARCH_ADDRESS;
  onAddressSelect?: (address: unknown) => void;
}

/**
 * Color picker field
 */
export interface ColorFormFieldConfig extends BaseFormFieldConfig {
  type: typeof FORM_FIELD_TYPES.COLOR;
}

/**
 * Image upload field
 */
export interface ImageFormFieldConfig extends BaseFormFieldConfig {
  type: typeof FORM_FIELD_TYPES.IMAGE;
  /** Display variant: "default" for dropzone, "avatar" for circular profile image */
  variant?: "default" | "avatar";
  /** Maximum file size in bytes (default: 3MB) */
  maxFileSize?: number;
  /** Minimum image width in pixels */
  minWidth?: number;
  /** Minimum image height in pixels */
  minHeight?: number;
  /** Maximum image width in pixels */
  maxWidth?: number;
  /** Maximum image height in pixels */
  maxHeight?: number;
  /**
   * Crop aspect ratio (width ÷ height). When set, overrides maxWidth/maxHeight for the cropper only.
   * Use when the backend enforces a bounding box but the crop should match a fixed shape (e.g. 1:1 icons).
   */
  cropAspectRatio?: number;
  /** Accepted MIME types (default: image/jpeg, image/png) */
  mimeType?: string;
  /** Sub help text displayed below the dropzone */
  subHelpText?: string;
  /** Size of the avatar in pixels (only used with variant="avatar", default: 150) */
  avatarSize?: number;
  /** Aria label for the edit button (only used with variant="avatar") */
  editButtonAriaLabel?: string;
  /** Custom error messages for validation errors */
  errorMessages?: {
    fileTooLarge?: string;
    fileInvalidType?: string;
    fileMinDimensions?: string;
    fileMaxDimensions?: string;
    tooManyFiles?: string;
  };
}

/**
 * Repeater item field configuration
 * Extends SingleFieldConfig but adds repeater-specific properties
 */
export interface RepeaterItemFieldConfig<T = Record<string, unknown>> {
  /** Base field configuration - can be any SingleFieldConfig type */
  field: Omit<SingleFieldConfig, "name" | "defaultValue">;
  /** Field name (will be nested under repeater name, e.g., "denominations[0].value") */
  name: string;
  /** Tooltip text shown next to the label in header */
  tooltip?: string;
  /** Flex width for column (e.g., "1", "2", "150px") */
  width?: string;
  /** Dynamic disabled state based on row data */
  disabled?: (rowData: T) => boolean;
  /** Dynamic tooltip based on row data (useful for explaining why field is disabled) */
  dynamicTooltip?: (rowData: T) => string | undefined;
}

/**
 * Repeater/Array field - for managing lists of items (e.g., denominations, ticket types)
 *
 * @example
 * {
 *   name: "denominations",
 *   type: FORM_FIELD_TYPES.REPEATER,
 *   label: "Price options",
 *   itemFields: [
 *     {
 *       name: "value",
 *       width: "1",
 *       tooltip: "The dollar value on the card",
 *       field: { type: FORM_FIELD_TYPES.NUMBER, label: "Value", required: true, min: 0, step: 0.01 }
 *     },
 *     {
 *       name: "price",
 *       width: "1",
 *       field: { type: FORM_FIELD_TYPES.NUMBER, label: "Price", required: true, min: 0, step: 0.01 }
 *     },
 *   ],
 *   defaultItem: { value: 0, price: 0, inventory: null },
 *   addButtonText: "Add option",
 * }
 */
export interface RepeaterFormFieldConfig extends BaseFormFieldConfig {
  type: typeof FORM_FIELD_TYPES.REPEATER;
  /** Configuration for fields in each row */
  itemFields: RepeaterItemFieldConfig[];
  /** Default values for new items */
  defaultItem?: Record<string, unknown>;
  /** Text for add button (default: "Add option") */
  addButtonText?: string;
  /** Whether items can be reordered (drag & drop) */
  sortable?: boolean;
  /** Minimum number of items required */
  minItems?: number;
  /** Maximum number of items allowed */
  maxItems?: number;
  /** Whether to show the header row with labels */
  showHeader?: boolean;
  /** Callback when an item is removed */
  onRemove?: (item: Record<string, unknown>, index: number) => void;
}

/**
 * Layout configuration for GROUP fields
 */
export interface GroupLayoutConfig {
  /** Layout direction: row (horizontal) or column (vertical) */
  direction?: ResponsiveValue<"row" | "column">;
  /** Gap between fields (e.g., "16px", "1rem") */
  gap?: ResponsiveValue<string>;
  /** Number of columns for CSS Grid layout (if set, uses grid instead of flex) */
  columns?: ResponsiveValue<number>;
  /** Flex wrap behavior */
  flexWrap?: ResponsiveValue<"wrap" | "nowrap">;
}

/**
 * Single field types (all non-group field types)
 */
export type SingleFieldConfig =
  | TextFormFieldConfig
  | EmailFormFieldConfig
  | PasswordFormFieldConfig
  | NumberFormFieldConfig
  | TextareaFormFieldConfig
  | RichtextFormFieldConfig
  | FontFamilyFormFieldConfig
  | PhoneFormFieldConfig
  | SelectFormFieldConfig
  | MultiSelectFormFieldConfig
  | DateFormFieldConfig
  | DateRangeFormFieldConfig
  | DatetimeFormFieldConfig
  | CheckboxFormFieldConfig
  | RadioFormFieldConfig
  | ToggleFormFieldConfig
  | SearchFormFieldConfig
  | SearchEventFormFieldConfig
  | SearchProductFormFieldConfig
  | SearchMembershipFormFieldConfig
  | SearchAddressFormFieldConfig
  | FileFormFieldConfig
  | ImageFormFieldConfig
  | ColorFormFieldConfig
  | RepeaterFormFieldConfig;

/**
 * GROUP field - groups multiple fields together with flexible layout
 *
 * @example
 * // Two fields in a row
 * {
 *   type: FORM_FIELD_TYPES.GROUP,
 *   layout: { direction: "row", gap: "16px" },
 *   fields: [
 *     { name: "firstName", type: FORM_FIELD_TYPES.TEXT, ... },
 *     { name: "lastName", type: FORM_FIELD_TYPES.TEXT, ... },
 *   ]
 * }
 *
 * @example
 * // Three fields in a grid
 * {
 *   type: FORM_FIELD_TYPES.GROUP,
 *   label: "Address",
 *   layout: { columns: 3, gap: "12px" },
 *   fields: [
 *     { name: "city", ... },
 *     { name: "state", ... },
 *     { name: "zip", ... },
 *   ]
 * }
 */
export interface GroupFormFieldConfig {
  type: typeof FORM_FIELD_TYPES.GROUP;
  /** Optional section label displayed above the group */
  label?: string;
  /** Help text displayed below the group */
  helpText?: string;
  /** Hide the entire group */
  hidden?: boolean;
  /** Layout configuration for the group */
  layout?: GroupLayoutConfig;
  /** Nested field configurations (cannot contain groups) */
  fields: SingleFieldConfig[];
}

/**
 * Section field - wraps child fields in a Card container
 * Used to visually group related fields together with layout control
 *
 * @example
 * {
 *   type: FORM_FIELD_TYPES.SECTION,
 *   title: "Gift Card Settings",
 *   fields: [
 *     { ...previewField, width: "33%" },
 *     {
 *       type: FORM_FIELD_TYPES.GROUP,
 *       width: "67%",
 *       fields: [nameField, imageField, colorField]
 *     }
 *   ],
 * }
 */
export interface SectionFormFieldConfig {
  type: typeof FORM_FIELD_TYPES.SECTION;
  /** Section title displayed at the top of the card */
  title?: string;
  /** Section description */
  description?: string;
  /** Child fields to render in this section */
  fields: Array<
    SingleFieldConfig | GroupFormFieldConfig | CustomFormFieldConfig
  >;
  /** Hide the entire section */
  hidden?: boolean;
  /** Layout configuration for fields within the section */
  layout?: GroupLayoutConfig;

  // Preview mode props
  /** Enable preview mode for this section - shows collapsed summary with Edit button */
  previewMode?: boolean;
  /**
   * Start in edit mode when all section fields are empty (default: true).
   * When true: Empty sections show form fields instead of preview.
   * When false: Always show preview mode regardless of data.
   */
  startInEditModeWhenEmpty?: boolean;
  /**
   * Custom render function for preview state.
   * Receives current field values and field configs for custom display.
   * If not provided, a default preview layout will be generated.
   */
  renderPreview?: (
    values: Record<string, unknown>,
    fieldConfigs: SingleFieldConfig[]
  ) => React.ReactNode;
  /** Text for the edit button (default: "Edit") */
  editButtonText?: string;
  /** Text for the save button (default: "Save") */
  saveButtonText?: string;
  /** Text for the cancel button (default: "Cancel") */
  cancelButtonText?: string;
  /**
   * Collapse back to preview after save (default: true).
   * When false: stay in edit mode after a successful save.
   */
  collapseOnSave?: boolean;
  /**
   * When true with previewMode: always show the edit form, never the collapsed preview.
   * No Edit button; use with collapseOnSave: false and hideCancelButton for "expanded only, Save only".
   */
  startInEditMode?: boolean;
  /** When true, do not render the Cancel button in the section footer. */
  hideCancelButton?: boolean;
  /** Callback when section is saved. If provided, called before collapsing back to preview. */
  onSectionSave?: (values: Record<string, unknown>) => Promise<void> | void;
}

/**
 * Custom field - renders a custom component
 * Used for previews, custom UI, etc.
 *
 * @example
 * {
 *   type: FORM_FIELD_TYPES.CUSTOM,
 *   width: "33%",
 *   render: ({ watch }) => <Preview name={watch("name")} />,
 * }
 */
export interface CustomFormFieldConfig {
  type: typeof FORM_FIELD_TYPES.CUSTOM;
  /** Render function - receives watch function for reactive values */
  render: (helpers: { watch: (name: string) => unknown }) => React.ReactNode;
  /** Hide the custom component */
  hidden?: boolean;
  /** Width of the custom component (e.g., "33%", "200px") */
  width?: string;
}

/**
 * FIELD_ARRAY field - allows multiple instances of a group of fields
 *
 * Creates an array field where each item contains the same set of sub-fields.
 * Useful for things like recipients, line items, or any repeatable data structure.
 *
 * @example
 * // Gift card recipients array
 * {
 *   type: FORM_FIELD_TYPES.FIELD_ARRAY,
 *   name: "recipients",
 *   minItems: 1,
 *   maxItems: 20,
 *   addButtonText: "Add another recipient",
 *   itemLabelTemplate: "Recipient {index}",
 *   fields: [
 *     { name: "recipient_name", type: FORM_FIELD_TYPES.TEXT, label: "Name", required: true },
 *     { name: "recipient_email", type: FORM_FIELD_TYPES.EMAIL, label: "Email", required: true },
 *     { name: "quantity", type: FORM_FIELD_TYPES.NUMBER, label: "Quantity", defaultValue: 1 },
 *   ]
 * }
 *
 * // Resulting data structure:
 * // {
 * //   recipients: [
 * //     { recipient_name: "John", recipient_email: "john@example.com", quantity: 1 },
 * //     { recipient_name: "Jane", recipient_email: "jane@example.com", quantity: 2 },
 * //   ]
 * // }
 */
export interface FieldArrayFormFieldConfig {
  type: typeof FORM_FIELD_TYPES.FIELD_ARRAY;
  /** Field name - this becomes the array property in form data */
  name: string;
  /** Optional section label displayed above the field array */
  label?: string;
  /** Help text displayed below the field array */
  helpText?: string;
  /** Hide the entire field array */
  hidden?: boolean;
  /** Test ID for the field array container */
  testId?: string;
  /**
   * Sub-fields that define the structure of each item in the array.
   * Each item will have all these fields.
   */
  fields: SingleFieldConfig[];
  /** Minimum number of items (default: 1) */
  minItems?: number;
  /** Maximum number of items */
  maxItems?: number;
  /** Text for the "Add" button (default: "Add Item") */
  addButtonText?: string;
  /** Aria-label for the remove button */
  removeButtonLabel?: string;
  /** Show item number/label above each item (default: true) */
  showItemLabel?: boolean;
  /** Custom item label template - use {index} for item number (e.g., "Recipient {index}") */
  itemLabelTemplate?: string;
  /** Layout configuration for fields within each item */
  itemLayout?: GroupLayoutConfig;
  /** Custom styling for each item's container */
  itemContainerProps?: {
    bg?: string;
    p?: string;
    borderRadius?: string;
  };
  /** Default values for the field array */
  defaultValues?: Record<string, unknown>;
}

/**
 * Union type of all possible form field configurations
 */
export type FormFieldConfig =
  | SingleFieldConfig
  | GroupFormFieldConfig
  | SectionFormFieldConfig
  | CustomFormFieldConfig
  | FieldArrayFormFieldConfig;
